// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useProposeCollateralAgreementChange } from '@hooks/mutations/collateral/collateralAgreements/useProposeCollateralAgreementChange';
import { useRequestCollateralAgreement } from '@hooks/mutations/collateral/collateralAgreements/useRequestCollateralAgreement';
import { useInstruments } from '@hooks/other/useInstruments';
import { useCollateralAgreementRequestsV2 } from '@hooks/queries/collateralv2/collateral/useCollateralAgreementRequestsV2';
import { useCollateralAgreementsV2 } from '@hooks/queries/collateralv2/collateral/useCollateralAgreementsV2';
import { useCollateralUserServiceV2 } from '@hooks/queries/collateralv2/onboarding/useUserService';
import React, { useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { CollateralAgreement } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { createInstrumentKey, InstrumentKey } from '../../../utils/instrument';
import { CollateralAgreementRow } from '../obligations/types';
import { InstrumentMultiSelect } from './InstrumentMultiSelect';
import { useRequestCollateralAgreementForm } from './useRequestCollateralAgreementForm';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  row?: CollateralAgreementRow;
};

const RequestCollateralAgreementDialog = ({ open, onClose, row }: DialogProps) => {
  const party = useModuleParty();

  const agreementIds = useCollateralAgreementsV2().data?.map((agreement) => agreement.payload.id);
  const pendingAgreementIds = useCollateralAgreementRequestsV2().data?.map(
    (pending) => pending.payload.id,
  );
  const allAgreementIds = agreementIds?.concat(pendingAgreementIds || []) ?? [];

  const { instruments, getInstrumentsByKeys } = useInstruments();

  const initialSelectedInstruments =
    row?.payload.terms.eligibleCollateral.eligibleInstruments.map(({ instrument: i }) => {
      return createInstrumentKey(i.id, i.admin);
    }) ?? [];

  const form = useRequestCollateralAgreementForm(
    row,
    party,
    allAgreementIds,
    initialSelectedInstruments,
  );

  const userService = useCollateralUserServiceV2();
  const requestCollateralAgreement = useRequestCollateralAgreement();
  const changeCollateralAgreement = useProposeCollateralAgreementChange();

  const resetState = useCallback(() => {
    if (row) {
      return;
    }
    form.setCounterparty('');
    form.setAgreementId('');
    form.setSelectedInstruments(new Set());
  }, [form, row]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleChange = useCallback(async () => {
    if (!userService.data) throw new Error('Trader service not found');
    if (!row?.contractId) throw new Error('No Collateral Agreement Cid defined');

    await changeCollateralAgreement({
      cid: userService.data.contractId,
      argument: {
        cid: row?.contractId as ContractId<CollateralAgreement>,
        updatedTerms: {
          eligibleCollateral: {
            eligibleInstruments: getInstrumentsByKeys(form.selectedInstruments).map((i) => ({
              instrument: i,
              haircut: '0',
            })),
          },
        },
      },
    });
    handleClose();
  }, [
    userService.data,
    handleClose,
    changeCollateralAgreement,
    row?.contractId,
    form,
    getInstrumentsByKeys,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!userService.data) throw new Error('Trader service not found');

    await requestCollateralAgreement({
      cid: userService.data.contractId,
      argument: {
        minimumTransferAmount: form.minimumTransferAmount,
        counterparty: form.counterparty,
        id: form.agreementId,
        requestorIsPartyA: true,
        terms: {
          eligibleCollateral: {
            eligibleInstruments: getInstrumentsByKeys(form.selectedInstruments).map((i) => ({
              instrument: i,
              haircut: '0',
            })),
          },
        },
      },
    });
    handleClose();
  }, [form, userService.data, handleClose, requestCollateralAgreement, getInstrumentsByKeys]);

  const onSelectInstrument = useCallback(
    (key: InstrumentKey) => {
      form.setSelectedInstruments((previousSelectedInstruments) => {
        const newSelectedInstruments = new Set(previousSelectedInstruments);

        if (newSelectedInstruments.has(key)) {
          newSelectedInstruments.delete(key);
        } else {
          newSelectedInstruments.add(key);
        }

        return newSelectedInstruments;
      });
    },
    [form],
  );

  const onRemoveInstrument = useCallback(
    (key: InstrumentKey) => {
      form.setSelectedInstruments((previousSelectedInstruments) => {
        const newSelectedInstruments = new Set(previousSelectedInstruments);
        newSelectedInstruments.delete(key);

        return newSelectedInstruments;
      });
    },
    [form],
  );

  const isLoading = userService.isLoading || !userService.data;

  if (isLoading) return <CircularProgress />;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormDialog
        open={open}
        title={row ? 'Modify collateral agreement' : 'Create a collateral agreement'}
        onCancel={handleClose}
        onSubmit={row ? handleChange : handleSubmit}
        submitName={row ? 'Modify' : 'Create'}
        isSubmitDisabled={form.isSubmitDisabled}
      >
        <TextInput
          enableTopLabel
          placeholder="Enter an identifier for this agreement"
          label="Agreement Id"
          value={form.agreementId}
          setValue={form.setAgreementId}
          validationMessage={form.agreementIdValidationMsg}
          getValidationMessage={form.validateAgreementId}
          setValidationMessage={form.setAgreementIdValidationMsg}
          disabled={!!row}
          required
        />
        <TextInput
          enableTopLabel
          placeholder="Enter the counterparty party ID"
          label="Counterparty (Party B)"
          caption="Enter the receiving party ID. By default, this will be Party B."
          value={form.counterparty}
          setValue={form.setCounterparty}
          validationMessage={form.counterpartyValidationMsg}
          setValidationMessage={form.setCounterpartyValidationMsg}
          getValidationMessage={form.validateCounterParty}
          disabled={!!row}
          required
        />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex' }}>
            <Typography
              fontWeight="bold"
              variant="caption"
              color="secondary"
              sx={{ textTransform: 'uppercase' }}
            >
              Eligible collateral
            </Typography>
            <Typography color="error">*</Typography>
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
            Select a collateral type for this agreement.
          </Typography>
        </Box>
        <InstrumentMultiSelect
          instruments={instruments}
          getInstrumentsByKeys={getInstrumentsByKeys}
          onRemove={onRemoveInstrument}
          selectedInstruments={Array.from(form.selectedInstruments)}
          onClick={onSelectInstrument}
        />
        <Box sx={{ pb: 2 }} />
        <TextInput
          enableTopLabel
          placeholder="Enter the minimum transfer amount"
          label="Minimum Transfer Amount"
          value={form.minimumTransferAmount}
          setValue={form.setMinimumTransferAmount}
          validationMessage={form.minimumTransferAmountValidationMsg}
          setValidationMessage={form.setMinimumTransferAmountValidationMsg}
          getValidationMessage={form.validateMinimumTransferAmount}
          disabled={!!row}
          type="number"
          required
        />
      </FormDialog>
    </LocalizationProvider>
  );
};
export const MemoizedRequestCollateralAgreementDialog = React.memo(
  RequestCollateralAgreementDialog,
);
